import { Box, Skeleton, Typography, useMediaQuery } from "@mui/material";
import dayjs from "dayjs";
import { FC, useMemo } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { InsureAssistPerformanceStyles } from "./InsureAssistPerformanceStyles";
import { useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";

interface IAverageTokensUsage {
  tokenUsageData: any;
}
const AverageTokenUsage: FC<IAverageTokensUsage> = ({ tokenUsageData }) => {
  const isMobile = useMediaQuery("(max-width:767px)");
  const { isLoading } = useAppSelector((store: RootState) => store.common);
  const formattedData = useMemo(() => {
    return tokenUsageData?.map((item: any) => ({
      date: dayjs(item.date).format("MM/DD HH:mm"),
      "Tokens Used": item.tokensUsed,
    }));
  }, [tokenUsageData]);

  return (
    <Box 
      sx={InsureAssistPerformanceStyles.averageTokenUsageContainer}
      id="average_token_usage_container"
      data-test-id="average_token_usage_container"
      aria-label="Average Token Usage Chart"
    >
      <Typography 
        sx={{ fontWeight: "600", marginBottom: 1 }}
        id="average_token_usage_title"
        data-test-id="average_token_usage_title"
        aria-label="Average Token Usage"
      >
        Average Token Usage
      </Typography>
      {isLoading ? (
        <Skeleton
          variant="rounded"
          height="250px"
          animation="wave"
          sx={{
            width: isMobile ? "100%" : "auto",
            minWidth: isMobile ? "100%" : "466px",
            flex: 1,
          }}
          id="average_token_usage_skeleton"
          data-test-id="average_token_usage_skeleton"
          aria-label="Loading Token Usage Chart"
        />
      ) : (
        <Box 
          sx={{ height: 300 }}
          id="average_token_usage_chart_container"
          data-test-id="average_token_usage_chart_container"
          aria-label="Token Usage Chart"
        >
          {!tokenUsageData?.length ? (
            <Box 
              sx={InsureAssistPerformanceStyles.noDataContainer}
              id="average_token_usage_no_data"
              data-test-id="average_token_usage_no_data"
              aria-label="No Token Usage Data Available"
            >
              <Typography
                sx={{ textAlign: "center", fontSize: 16, color: "black" }}
                id="average_token_usage_no_data_text"
                data-test-id="average_token_usage_no_data_text"
                aria-label="No records found"
              >
                No records found
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer 
              width="100%" 
              height="100%"
              id="average_token_usage_chart"
              data-test-id="average_token_usage_chart"
              aria-label="Token Usage Line Chart"
            >
              <LineChart data={formattedData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" dy={10} />
                <YAxis />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="Tokens Used"
                  stroke="black"
                  strokeWidth={1}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AverageTokenUsage;
